'use client'

/**
 * Deploy-skew handling shared by every error boundary (`PageError`, `SectionErrorFallback`,
 * `global-error.tsx`) — Sentry RANKSURF-8G.
 *
 * A tab on an old deployment posts a Server Action id the new one no longer has. A reload fixes it,
 * so each boundary shows a reload prompt instead of a dead "Try again" (`unstable_retry` re-renders
 * on the same stale bundle). It is not an alert: Sentry ignores the message (`sentry-noise-filters`)
 * and the rate per deploy is read from PostHog (`deploy_skew_reload_prompt`), not Sentry quota.
 *
 * If skew recurs right after the user pressed Reload it is not skew — the action is missing from the
 * *current* build, or the input was manipulated. That is a real bug: it is captured as a distinct
 * exception the ignore filter does not match, and the boundary shows its ordinary card.
 */

import { useEffect, useMemo, useRef } from 'react'
import * as Sentry from '@sentry/nextjs'
import posthog from 'posthog-js'
import { captureAnalyticsEvent } from '@/lib/analytics/events'
import {
  classifyBoundaryError,
  markSkewReload,
  type BoundaryErrorKind,
} from '@/lib/observability/server-action-skew'

export const SKEW_PROMPT_EVENT = 'deploy_skew_reload_prompt' as const

/**
 * A pathname with its id- and token-like segments replaced by `:id`, so the analytics event names
 * the route, never a project/prompt id or a report/claim token (`/report/<token>` → `/report/:id`).
 * A segment counts as an id when it is a UUID, all digits, or 16+ characters of [A-Za-z0-9_-].
 */
function routeTemplate(pathname: string): string {
  return pathname
    .split('/')
    .map((segment) =>
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment) ||
      /^\d+$/.test(segment) ||
      /^[A-Za-z0-9_-]{16,}$/.test(segment)
        ? ':id'
        : segment
    )
    .join('/')
}

/** The one place a full page reload is issued — also records it so a recurrence is recognisable. */
function reloadForSkew(): void {
  markSkewReload()
  window.location.reload()
}

/**
 * Reports one handled skew: a PostHog event (the measurable rate) plus a context breadcrumb.
 * Returns whether the analytics event was handed to PostHog — a failed capture must never break
 * an error boundary, so it is reported by the return value, not thrown.
 */
export function recordSkewPrompt(route: string): boolean {
  Sentry.addBreadcrumb({
    category: 'deploy-skew',
    level: 'info',
    message: 'Server Action not found on this deployment',
    data: { route },
  })
  try {
    captureAnalyticsEvent(posthog, SKEW_PROMPT_EVENT, { route: routeTemplate(route) })
    return true
  } catch {
    return false
  }
}

/**
 * Skew that survived a reload. Deliberately a *new* error whose message does not match
 * `SERVER_ACTION_SKEW_PATTERN`, so the ignore filter lets it through; the original goes in context.
 */
function captureSkewRecurrence(error: Error, route: string): void {
  const wrapped = new Error('Deploy skew persisted after a reload — action missing from this build')
  wrapped.name = 'DeploySkewPersistedError'
  Sentry.withScope((scope) => {
    scope.setTag('route', route)
    scope.setContext('skew', { originalName: error.name, originalMessage: error.message })
    Sentry.captureException(wrapped)
  })
}

/** Side effects for a classified boundary error; `ordinary` is left to the boundary's own capture. */
export function reportBoundaryKind(kind: BoundaryErrorKind, error: Error, route: string): void {
  if (kind === 'skew') recordSkewPrompt(route)
  else if (kind === 'recurred') captureSkewRecurrence(error, route)
}

export function useSkewAwareError(
  error: Error,
  route: string
): { kind: BoundaryErrorKind; isSkew: boolean; onReload: () => void } {
  const kind = useMemo(() => classifyBoundaryError(error), [error])
  // Latest-ref: a navigation without a new error must not re-report.
  const routeRef = useRef(route)
  useEffect(() => {
    routeRef.current = route
  }, [route])
  useEffect(() => {
    reportBoundaryKind(kind, error, routeRef.current)
  }, [error, kind])
  return { kind, isSkew: kind === 'skew', onReload: reloadForSkew }
}

export const SKEW_TITLE = 'A new version of RankSurf is available'
export const SKEW_DESCRIPTION =
  "Reload to continue — what you were doing wasn't saved, and your data hasn't changed."
